// @flow
import i18n from "i18next";
import k from "src/i18n/keys";

import React, { useState, useCallback, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Link from "redux-first-router-link";
import {
  Box,
  FormControl,
  FormLabel,
  Button,
  Stack,
  IconButton,
} from "@chakra-ui/react";

import NavLinks from "src/components/auth/NavLinks";
import { ForgotPasswordLink } from "src/styles/auth";
import * as styles from "src/styles/auth";
import { rehydrateChatroom } from "src/actions";
import {
  authRequest,
  getAuthProvider,
  clearAuthProvider,
  clearAllErrors,
  setCurrentUserEmail,
} from "src/actions/auth";
import { getSignInError, getSignInEmail } from "src/reducers";
import { getAuthProvider as getAuthProviderSelector } from "src/selectors";
import Icon from "src/icons";
import { Input } from "@unifize/sarah";
import SignInError from "src/components/auth/SignInError";
import { getErrorMessage } from "src/components/auth/utils";

const SignIn = () => {
  const dispatch = useDispatch();
  const error = useSelector((state) => getSignInError("login")(state.app));
  const currentEmail = useSelector((state) => getSignInEmail(state.app));
  const authProvider = useSelector((state) => getAuthProviderSelector(state));
  const emailRef = useRef(null);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const showErrorMessage = error && error.status !== 403;

  useEffect(() => {
    // Hide the loader if the state of the form has been changed
    // i.e password field is visible
    if (authProvider !== undefined) {
      setLoading(false);
    } else if (authProvider !== null && `${authProvider ?? ""}`.length > 0) {
      setLoading(true);
    }
  }, [authProvider]);

  useEffect(() => {
    if (currentEmail && emailRef.current) {
      emailRef.current.value = currentEmail;
    }
  }, [currentEmail, emailRef.current]);

  useEffect(() => {
    // Hide the loader if error is encountered
    if (showErrorMessage) {
      setLoading(false);
    }
  }, [showErrorMessage]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      try {
        setLoading(true);
        if (emailRef.current) {
          const { value: email } = emailRef.current;
          if (email && !password) {
            dispatch(getAuthProvider(email));
          } else if (email && password) {
            dispatch(authRequest({ email, password }));
            dispatch(rehydrateChatroom());
          }
        }
      } catch (e) {
        console.error(e);
      }
    },
    [dispatch, password],
  );

  const handleBackClick = () => {
    setPassword("");
    dispatch(clearAuthProvider());
    dispatch(clearAllErrors());
  };

  const signInDisabled = authProvider === null && !password.length;
  const errorMessageObject = getErrorMessage(error.message);
  const errorMessage =
    showErrorMessage && errorMessageObject.isEmailOrWrongPassword
      ? errorMessageObject.messageToBeDisplayed
      : null;

  return (
    <>
      <Box sx={styles.AuthContainer}>
        {authProvider === null && (
          <IconButton
            marginBottom={2}
            icon={<Icon type="back" />}
            onClick={handleBackClick}
          />
        )}
        <form onSubmit={handleSubmit}>
          <Stack spacing={4}>
            <FormControl id="email">
              <FormLabel>{i18n.t(k.EMAIL_ADDRESS)}</FormLabel>
              <Input
                type="email"
                id="email"
                name="email"
                ref={emailRef}
                placeholder={i18n.t(k.EMAIL_PLACEHOLDER)}
                onChange={(e) => dispatch(setCurrentUserEmail(e.target.value))}
                required
                readOnly={authProvider !== undefined}
              />
            </FormControl>
            {authProvider === null && (
              <FormControl id="password">
                <FormLabel>{i18n.t(k.PASSWORD)} </FormLabel>
                <Input
                  type="password"
                  id="password"
                  name="password"
                  onChange={(e) => setPassword(e.target.value)}
                  error={errorMessage}
                  required
                />

                <ForgotPasswordLink>
                  <Link to="/signin/forgot">{i18n.t(k.FORGOT_PASSWORD)}</Link>
                </ForgotPasswordLink>
              </FormControl>
            )}
          </Stack>

          {showErrorMessage && <SignInError error={error.message} />}

          <Button
            variant="uPrimary"
            sx={styles.AuthButton}
            type="submit"
            isLoading={
              loading || (authProvider !== null && authProvider !== undefined)
            }
            name="next"
            isDisabled={signInDisabled}
          >
            <span>{i18n.t(k.NEXT)}</span>
          </Button>
        </form>
      </Box>
      <NavLinks />
    </>
  );
};

export default SignIn;
